import React from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Button from '@mui/material/Button';
import Collapse from '@mui/material/Collapse';
import CircularProgress from '@mui/material/CircularProgress';
import {
  OpenInNew, LinkOff, InsertDriveFile, PictureAsPdf, Image as ImageIcon, Add, ExpandLess, DrawOutlined,
} from '@mui/icons-material';
import firebase from '../utilities/firebase';
import store from '../utilities/store';
import { StateManager } from '../utilities/stateManager';
import FileUploader from './FileUploader';
import CustomSignatureDialog from './CustomSignatureDialog';
import SignatureStatusDialog from './CarModal/SignatureStatusDialog';

const keyOf = (l) => `${l.type}:${l.id}`;
const TYPE_LABEL = { car: 'car', customer: 'customer', serviceOrder: 'service order', user: 'user' };
// Where one-off signature requests for each signable entity are recorded.
const SIGN_SOURCES = {
  car: { table: 'cars', docType: 'custom' },
  serviceOrder: { table: 'orders', docType: 'custom_service' },
};
const PENDING_SIGN = ['sent', 'partially signed'];

const isImage = (ct = '') => ct.startsWith('image/');
const isPdf = (ct = '') => ct === 'application/pdf';
function FileIcon({ contentType }) {
  if (isImage(contentType)) return <ImageIcon color="action" />;
  if (isPdf(contentType)) return <PictureAsPdf color="error" />;
  return <InsertDriveFile color="action" />;
}

// Renders the files linked to a single entity (car / customer / service order /
// user) and lets the user add more, pre-linked to that entity. Backed by the
// shared Document Library `files` collection via Store.getEntityFiles.
//
// Props: { type, id, label, legacyStock, readOnly, signContacts }
//   legacyStock  – optional; also pulls in pre-linkKeys files keyed by `stock`.
//   readOnly     – optional; hides add, unlink and sign but keeps Open.
//   signContacts – optional; [{ label, name, email }]. When set (cars and
//                  service orders), PDFs get a "Request signature" action.
export default function EntityFiles({ type, id, label, legacyStock, readOnly = false, signContacts }) {
  const [files, setFiles] = React.useState(null);
  const [showUpload, setShowUpload] = React.useState(false);
  const [signFile, setSignFile] = React.useState(null);
  const [signRequests, setSignRequests] = React.useState([]);
  const [statusOpen, setStatusOpen] = React.useState(false);
  const signSource = signContacts ? SIGN_SOURCES[type] : null;

  const load = React.useCallback(async () => {
    if (!id) { setFiles([]); return; }
    try {
      const list = await store.getEntityFiles({ type, id, legacyStock });
      list.sort((a, b) =>
        (b.uploadedAt || 0) - (a.uploadedAt || 0) || String(b.date || '').localeCompare(String(a.date || '')));
      setFiles(list);
    } catch (err) {
      console.error(err);
      StateManager.setAlertAndOpen('Could not load files', 'error');
      setFiles([]);
    }
  }, [type, id, legacyStock]);

  React.useEffect(() => { setFiles(null); load(); }, [load]);

  // Live one-off signature requests. A request that completes files its signed
  // copy into the library, so reload the list when the signed count goes up.
  const signedCount = React.useRef(null);
  React.useEffect(() => {
    if (!signSource || !id) return undefined;
    signedCount.current = null;
    return firebase.firestore()
      .collection(`${signSource.table}/${id}/signatures`)
      .where('type', '==', signSource.docType)
      .onSnapshot((snap) => {
        const recs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        const signed = recs.filter(r => r.status === 'signed').length;
        if (signedCount.current !== null && signed > signedCount.current) load();
        signedCount.current = signed;
        setSignRequests(recs);
      }, (err) => console.error(err));
  }, [signSource && signSource.table, signSource && signSource.docType, id, load]); // eslint-disable-line react-hooks/exhaustive-deps

  const pendingSourceIds = new Set(signRequests
    .filter(r => PENDING_SIGN.includes(r.status) && r.sourceFileId)
    .map(r => r.sourceFileId));

  const lockedLinks = id ? [{ type, id, label: label || id }] : [];
  const typeLabel = TYPE_LABEL[type] || 'record';

  const open = async (file) => {
    try {
      const url = await store.getFileDownloadURL(file.id);
      const a = document.createElement('a');
      a.href = url;
      a.target = '_blank';
      a.click();
    } catch (err) {
      console.error(err);
      StateManager.setAlertAndOpen('Could not open file', 'error');
    }
  };

  // Unlink (not delete): removes this entity from the file's links so it leaves
  // this tab but stays in the Document Library and any other linked records.
  const unlink = async (file) => {
    if (!window.confirm(`Remove "${file.name}" from this ${typeLabel}?`)) return;
    StateManager.setLoading(true);
    try {
      const remaining = (file.links || []).filter(l => keyOf(l) !== `${type}:${id}`);
      await store.updateLibraryFile(file.id, { links: remaining });
      setFiles(prev => (prev || []).filter(f => f.id !== file.id));
    } catch (err) {
      console.error(err);
      StateManager.setAlertAndOpen('Could not remove file', 'error');
    } finally {
      StateManager.setLoading(false);
    }
  };

  const handleUploaded = (uploaded) => {
    setFiles(prev => [uploaded, ...(prev || []).filter(f => f.id !== uploaded.id)]);
  };

  if (files === null) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>;
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
        <Typography variant="subtitle2" color="text.secondary">
          {files.length} file{files.length === 1 ? '' : 's'}
        </Typography>
        <Box sx={{ flex: 1 }} />
        {type === 'car' && signSource && signRequests.length > 0 && (
          <Button size="small" startIcon={<DrawOutlined />} onClick={() => setStatusOpen(true)} sx={{ mr: 1 }}>
            Signature requests ({signRequests.length})
          </Button>
        )}
        {!readOnly && <Button
          size="small"
          startIcon={showUpload ? <ExpandLess /> : <Add />}
          onClick={() => setShowUpload(s => !s)}
        >
          {showUpload ? 'Done' : 'Add files'}
        </Button>}
      </Box>

      <Collapse in={showUpload && !readOnly} unmountOnExit>
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <FileUploader compact lockedLinks={lockedLinks} onUploaded={handleUploaded} />
        </Paper>
      </Collapse>

      {files.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
          No files linked to this {typeLabel} yet.
        </Typography>
      ) : (
        <Paper variant="outlined">
          {files.map((file, idx) => (
            <Box
              key={file.id}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                px: 2,
                py: 1,
                borderBottom: idx === files.length - 1 ? 'none' : '1px solid',
                borderColor: 'divider',
              }}
            >
              <FileIcon contentType={file.contentType} />
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography variant="body2" noWrap title={file.name}>{file.name}</Typography>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap', gap: 0.5, mt: 0.25 }}>
                  {file.category && (
                    <Chip size="small" label={file.category} sx={{ textTransform: 'capitalize', height: 20 }} />
                  )}
                  {pendingSourceIds.has(file.id) && (
                    <Chip size="small" color="info" variant="outlined" label="Out for signature" sx={{ height: 20 }} />
                  )}
                  {(file.tags || []).map(t => (
                    <Chip key={t} size="small" variant="outlined" label={t} sx={{ height: 20 }} />
                  ))}
                  <Typography variant="caption" color="text.secondary">{file.date}</Typography>
                </Stack>
              </Box>
              {signContacts && !readOnly && isPdf(file.contentType) && file.status !== 'signed' && (
                <Tooltip title="Request signature">
                  <IconButton size="small" onClick={() => setSignFile(file)}><DrawOutlined fontSize="small" /></IconButton>
                </Tooltip>
              )}
              <Tooltip title="Open"><IconButton size="small" onClick={() => open(file)}><OpenInNew fontSize="small" /></IconButton></Tooltip>
              {!readOnly && (
                <Tooltip title={`Remove from this ${typeLabel}`}>
                  <IconButton size="small" color="primary" onClick={() => unlink(file)}><LinkOff fontSize="small" /></IconButton>
                </Tooltip>
              )}
            </Box>
          ))}
        </Paper>
      )}

      {signFile && (
        <CustomSignatureDialog
          open
          onClose={() => { setSignFile(null); load(); }}
          file={signFile}
          parentType={type}
          refId={id}
          contacts={signContacts}
        />
      )}

      {statusOpen && (
        <SignatureStatusDialog
          open
          onClose={() => setStatusOpen(false)}
          car={{ stock: id }}
          type={signSource.docType}
          table={signSource.table}
          label="One-off document"
        />
      )}
    </Box>
  );
}
